import { useState, useRef, useEffect } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart
} from "recharts";
import {
  Home, ShoppingCart, Users, Printer, Search,
  DollarSign, Hash, Zap, Star, TrendingUp, Clock,
  CheckCircle, BarChart3, FileText, Bell, ChevronDown,
  Filter, Settings, Calendar, ChevronLeft, ChevronRight,
  X, Package, Truck, AlertCircle, XCircle, SlidersHorizontal,
  RefreshCcw, Menu, Shirt, Coffee, Layers
} from "lucide-react";

const C = {
  teal:       "#0F766E",
  tealDark:   "#0D5F58",
  tealLight:  "#14B8A6",
  bg:         "#FFF8EF",
  white:      "#FFFFFF",
  text:       "#1C2B2A",
  textMid:    "#4B6B67",
  textSoft:   "#8AADA9",
  border:     "#E8DDD0",
  accent:     "#F0A86B",
  accentSoft: "#FAE3CC",
  purple:     "#6B4FAF",
  glass:      "rgba(255,255,255,0.92)",
  glassBorder:"rgba(232,221,208,0.6)",
  shadow:     "0 4px 24px rgba(15,118,110,0.10)",
  shadowHover:"0 8px 32px rgba(15,118,110,0.16)",
};
const BARS = [C.teal, C.tealLight, C.accent, C.purple, "#E06C3A", "#A8D5D1"];

// ─── Current date helpers ──────────────────────────────────────────────────────
const TODAY = new Date();
const CURRENT_YEAR  = TODAY.getFullYear();
const CURRENT_MONTH = TODAY.getMonth() + 1; // 1-based
const CURRENT_DAY   = TODAY.getDate();

const ALL_DATA = {
  2023: {
    monthly: [
      {m:"Jan",month:1,rev:38000,orders:28,date:"2023-01-15"},{m:"Feb",month:2,rev:42000,orders:32,date:"2023-02-15"},
      {m:"Mar",month:3,rev:41000,orders:36,date:"2023-03-15"},{m:"Apr",month:4,rev:39000,orders:30,date:"2023-04-15"},
      {m:"May",month:5,rev:44000,orders:38,date:"2023-05-15"},{m:"Jun",month:6,rev:55000,orders:40,date:"2023-06-15"},
      {m:"Jul",month:7,rev:51000,orders:42,date:"2023-07-15"},{m:"Aug",month:8,rev:49000,orders:35,date:"2023-08-15"},
      {m:"Sep",month:9,rev:46000,orders:34,date:"2023-09-15"},{m:"Oct",month:10,rev:48000,orders:39,date:"2023-10-15"},
      {m:"Nov",month:11,rev:52000,orders:44,date:"2023-11-15"},{m:"Dec",month:12,rev:61000,orders:51,date:"2023-12-15"},
    ],
  },
  2024: {
    monthly: [
      {m:"Jan",month:1,rev:55635,orders:49,date:"2024-01-15"},{m:"Feb",month:2,rev:56123,orders:31,date:"2024-02-15"},
      {m:"Mar",month:3,rev:44015,orders:42,date:"2024-03-15"},{m:"Apr",month:4,rev:41516,orders:38,date:"2024-04-15"},
      {m:"May",month:5,rev:42932,orders:47,date:"2024-05-15"},{m:"Jun",month:6,rev:69162,orders:46,date:"2024-06-15"},
      {m:"Jul",month:7,rev:47100,orders:43,date:"2024-07-15"},{m:"Aug",month:8,rev:42704,orders:36,date:"2024-08-15"},
      {m:"Sep",month:9,rev:39780,orders:31,date:"2024-09-15"},{m:"Oct",month:10,rev:31726,orders:31,date:"2024-10-15"},
      {m:"Nov",month:11,rev:51022,orders:46,date:"2024-11-15"},{m:"Dec",month:12,rev:43112,orders:43,date:"2024-12-15"},
    ],
  },
  2025: {
    monthly: [
      {m:"Jan",month:1,rev:62000,orders:54,date:"2025-01-15"},{m:"Feb",month:2,rev:58000,orders:48,date:"2025-02-15"},
      {m:"Mar",month:3,rev:71000,orders:60,date:"2025-03-15"},{m:"Apr",month:4,rev:65000,orders:55,date:"2025-04-15"},
      {m:"May",month:5,rev:74000,orders:62,date:"2025-05-15"},{m:"Jun",month:6,rev:80000,orders:68,date:"2025-06-15"},
      {m:"Jul",month:7,rev:77000,orders:65,date:"2025-07-15"},{m:"Aug",month:8,rev:69000,orders:59,date:"2025-08-15"},
      {m:"Sep",month:9,rev:72000,orders:61,date:"2025-09-15"},{m:"Oct",month:10,rev:68000,orders:57,date:"2025-10-15"},
      {m:"Nov",month:11,rev:75000,orders:64,date:"2025-11-15"},{m:"Dec",month:12,rev:85000,orders:71,date:"2025-12-15"},
    ],
  }
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_OF_WEEK = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// ─── Check if a given date (year, month 1-based, day) is in the future ─────────
function isFutureDate(year, month, day) {
  const d = new Date(year, month - 1, day);
  const todayMidnight = new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate());
  return d > todayMidnight;
}

function generateDayData(year, month) {
  const days = new Date(year, month, 0).getDate();
  const result = {};
  const seed = (year * 100 + month);
  for (let d = 1; d <= days; d++) {
    const x = ((seed + d * 7) % 13) + 1;
    result[d] = {
      placed:    x,
      delivered: Math.max(0, x - ((d % 3))),
      pending:   (d % 4 === 0) ? 2 : 1,
      cancelled: (d % 7 === 0) ? 1 : 0,
      revenue:   x * 800 + (d % 5) * 200,
    };
  }
  return result;
}

// ─── Core date range filtering logic ──────────────────────────────────────────
function deriveData(filter) {
  const { year, month, day, fromDate, toDate } = filter;

  let allMonthly = [];
  Object.entries(ALL_DATA).forEach(([y, yd]) => {
    yd.monthly.forEach(r => allMonthly.push({ ...r, year: Number(y) }));
  });

  if (fromDate || toDate) {
    const from = fromDate ? new Date(fromDate) : null;
    const to   = toDate   ? new Date(toDate)   : null;

    allMonthly = allMonthly.filter(r => {
      const rowStart = new Date(r.year, r.month - 1, 1);
      const rowEnd   = new Date(r.year, r.month, 0);
      if (from && rowEnd   < from) return false;
      if (to   && rowStart > to)   return false;
      return true;
    });
  } else {
    const targetYear  = year  !== "all" ? Number(year)  : null;
    const targetMonth = month !== "all" ? Number(month) : null;

    allMonthly = allMonthly.filter(r => {
      if (targetYear  && r.year  !== targetYear)  return false;
      if (targetMonth && r.month !== targetMonth) return false;
      return true;
    });
  }

  const monthly = allMonthly.map(r => ({ m: r.m, month: r.month, rev: r.rev, orders: r.orders }));
  const totalRevenue = monthly.reduce((s, r) => s + r.rev, 0);
  const totalOrders  = monthly.reduce((s, r) => s + r.orders, 0);
  const avgOrder     = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;
  const biggestOrder = Math.round(totalRevenue * 0.018);

  const completedOrders = Math.round(totalOrders * 0.602);
  const openOrders      = Math.round(totalOrders * 0.234);
  const rushOrders      = Math.round(totalOrders * 0.088);

  const base = 564827;
  const factor = totalRevenue / base;

  const orderTypes = [
    {name:"Banners",  rev:Math.round(113778 * factor)},
    {name:"Booklets", rev:Math.round(85776  * factor)},
    {name:"Brochures",rev:Math.round(75316  * factor)},
    {name:"Calendars",rev:Math.round(60876  * factor)},
    {name:"T-Shirts", rev:Math.round(54202  * factor)},
    {name:"Posters",  rev:Math.round(44528  * factor)},
  ];

  // Updated service categories: Digital Print, Clothing, Mugs, Resin
  const serviceRevenue = [
    {name:"Digital Print", rev:Math.round(158306 * factor), units:Math.round(223041 * factor)},
    {name:"Clothing",      rev:Math.round(197422 * factor), units:Math.round(187937 * factor)},
    {name:"Mugs",          rev:Math.round(114897 * factor), units:Math.round(122277 * factor)},
    {name:"Resin",         rev:Math.round(94202  * factor), units:Math.round(83447  * factor)},
  ];

  const segmentRevData = [
    {name:"Corporate", rev:Math.round(170884 * factor)},
    {name:"Government",rev:Math.round(56081  * factor)},
    {name:"Individual",rev:Math.round(67235  * factor)},
    {name:"Non-Profit",rev:Math.round(63459  * factor)},
    {name:"Small Biz", rev:Math.round(207168 * factor)},
  ];

  return {
    monthly, totalRevenue, totalOrders, avgOrder, biggestOrder,
    completedOrders, openOrders, rushOrders,
    orderTypes, serviceRevenue, segmentRevData, factor,
  };
}

const fmt = (n) => "$" + n.toLocaleString();

// ─── Today's date string for date inputs ───────────────────────────────────────
function todayString() {
  const y = TODAY.getFullYear();
  const m = String(TODAY.getMonth() + 1).padStart(2, "0");
  const d = String(TODAY.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ─── DEFAULT_FILTER uses current date ─────────────────────────────────────────
const DEFAULT_FILTER = {
  year:     CURRENT_YEAR,
  month:    CURRENT_MONTH,
  day:      CURRENT_DAY,
  fromDate: todayString(),
  toDate:   todayString(),
};

// ─── useBreakpoint ─────────────────────────────────────────────────────────────
function useBreakpoint() {
  const [bp, setBp] = useState(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1200;
    return w < 480 ? "xs" : w < 768 ? "sm" : w < 1024 ? "md" : "lg";
  });
  useEffect(() => {
    const handler = () => {
      const w = window.innerWidth;
      setBp(w < 480 ? "xs" : w < 768 ? "sm" : w < 1024 ? "md" : "lg");
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return bp;
}

// ─── KpiCard ───────────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, tint, sub }) {
  const bg  = tint === "accent" ? C.accentSoft : tint === "purple" ? "#EDE8F8" : "#E6F4F3";
  const clr = tint === "accent" ? C.accent     : tint === "purple" ? C.purple  : C.teal;
  return (
    <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:14,
      padding:"14px 16px", display:"flex", alignItems:"center", gap:12,
      boxShadow:"0 1px 6px rgba(15,118,110,0.06)", transition:"box-shadow .2s, transform .2s" }}
      onMouseEnter={e=>{ e.currentTarget.style.boxShadow=C.shadowHover; e.currentTarget.style.transform="translateY(-2px)"; }}
      onMouseLeave={e=>{ e.currentTarget.style.boxShadow="0 1px 6px rgba(15,118,110,0.06)"; e.currentTarget.style.transform="translateY(0)"; }}>
      <div style={{ width:40, height:40, borderRadius:11, background:bg,
        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Icon size={18} color={clr} />
      </div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:10, fontWeight:600, color:C.textSoft, letterSpacing:"0.06em",
          textTransform:"uppercase", marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{label}</div>
        <div style={{ fontSize:18, fontWeight:800, color:C.text, letterSpacing:"-0.02em",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{value}</div>
        {sub && <div style={{ fontSize:10, color:C.teal, marginTop:1, fontWeight:600 }}>{sub}</div>}
      </div>
    </div>
  );
}

function Card({ title, children, style }) {
  return (
    <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:14,
      padding:"16px 14px 12px", boxShadow:"0 1px 6px rgba(15,118,110,0.06)", ...style }}>
      {title && <div style={{ fontSize:12, fontWeight:700, color:C.text,
        marginBottom:12, letterSpacing:"-0.01em" }}>{title}</div>}
      {children}
    </div>
  );
}

// ─── FilterDropdown ────────────────────────────────────────────────────────────
function FilterDropdown({ label, value, options, onChange, width=130 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const displayLabel = options.find(o => String(o.value) === String(value))?.label || value;
  const isActive = String(value) !== "all";
  return (
    <div ref={ref} style={{ position:"relative", width, flexShrink:0 }}>
      <button onClick={() => setOpen(!open)} style={{
        width:"100%", padding:"7px 10px", borderRadius:10,
        border:`1.5px solid ${isActive ? C.teal : C.border}`,
        background: isActive ? `rgba(15,118,110,0.06)` : C.white,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        cursor:"pointer", fontSize:12, fontWeight:600,
        color: isActive ? C.teal : C.textMid,
        transition:"all .18s",
        boxShadow: open ? `0 0 0 3px rgba(15,118,110,0.12)` : "none",
      }}>
        <span style={{ display:"flex", alignItems:"center", gap:4, overflow:"hidden" }}>
          <span style={{ fontSize:9.5, fontWeight:500, color:C.textSoft, flexShrink:0 }}>{label}:</span>
          <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{displayLabel}</span>
        </span>
        <ChevronDown size={12} color={isActive ? C.teal : C.textSoft}
          style={{ transform:open?"rotate(180deg)":"none", transition:"transform .2s", flexShrink:0, marginLeft:4 }} />
      </button>
      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 5px)", left:0, width:"100%", minWidth:130,
          background:C.white, border:`1.5px solid ${C.border}`, borderRadius:12,
          overflow:"auto", maxHeight:220, zIndex:300,
          boxShadow:"0 8px 32px rgba(15,118,110,0.14)",
        }}>
          {options.map(opt => (
            <div key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                padding:"8px 12px", cursor:"pointer",
                background: String(value) === String(opt.value) ? `rgba(15,118,110,0.08)` : "transparent",
                fontSize:12, fontWeight: String(value) === String(opt.value) ? 700 : 500,
                color: String(value) === String(opt.value) ? C.teal : C.text,
                borderBottom:`1px solid ${C.border}`,
              }}>
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CalendarPopup ─────────────────────────────────────────────────────────────
function CalendarPopup({ filter, onSelectDate, onClose }) {
  const [viewYear,  setViewYear]  = useState(filter.year  !== "all" ? Number(filter.year)  : CURRENT_YEAR);
  const [viewMonth, setViewMonth] = useState((filter.month !== "all" ? Number(filter.month) : CURRENT_MONTH) - 1);
  const [hoverDay,  setHoverDay]  = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";

  const dayData = generateDayData(viewYear, viewMonth + 1);
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const DOT_TYPES = [
    { key:"placed",    color:C.teal,    label:"Placed" },
    { key:"delivered", color:"#22c55e", label:"Delivered" },
    { key:"pending",   color:C.accent,  label:"Pending" },
    { key:"cancelled", color:"#ef4444", label:"Cancelled" },
  ];

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1000,
      display:"flex", alignItems:isMobile?"flex-end":"flex-start",
      justifyContent:isMobile?"center":"flex-end",
      paddingTop:isMobile?0:80, paddingRight:isMobile?0:28,
      background: isMobile ? "rgba(0,0,0,0.4)" : "transparent",
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background:C.white, borderRadius:isMobile?"20px 20px 0 0":20,
        width:isMobile?"100%":420, maxWidth:"100%",
        boxShadow:"0 20px 60px rgba(15,118,110,0.18)",
        border:`1.5px solid ${C.border}`,
        overflow:"hidden",
        maxHeight:isMobile?"90vh":"auto",
        overflowY:"auto",
      }}>
        <div style={{ background:`linear-gradient(135deg, ${C.teal} 0%, ${C.tealDark} 100%)`,
          padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={prevMonth} style={{ background:"rgba(255,255,255,0.15)", border:"none",
              borderRadius:8, width:28, height:28, display:"flex", alignItems:"center",
              justifyContent:"center", cursor:"pointer", color:C.white }}>
              <ChevronLeft size={14} />
            </button>
            <span style={{ fontSize:14, fontWeight:700, color:C.white, minWidth:150, textAlign:"center" }}>
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button onClick={nextMonth} style={{ background:"rgba(255,255,255,0.15)", border:"none",
              borderRadius:8, width:28, height:28, display:"flex", alignItems:"center",
              justifyContent:"center", cursor:"pointer", color:C.white }}>
              <ChevronRight size={14} />
            </button>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,0.15)", border:"none",
            borderRadius:8, width:28, height:28, display:"flex", alignItems:"center",
            justifyContent:"center", cursor:"pointer", color:C.white }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, padding:"10px 12px 4px",
          background:`rgba(15,118,110,0.04)` }}>
          {DAYS_OF_WEEK.map(d => (
            <div key={d} style={{ textAlign:"center", fontSize:9.5, fontWeight:700,
              color:C.textSoft, padding:"4px 0" }}>{d}</div>
          ))}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, padding:"4px 12px 12px" }}>
          {Array.from({ length: firstDay }, (_, i) => <div key={`blank-${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const data = dayData[day];
            const isHover    = hoverDay === day;
            const isSelected = selectedDay === day;
            const isFuture   = isFutureDate(viewYear, viewMonth + 1, day);

            return (
              <div key={day}
                onClick={() => {
                  if (!isFuture) {
                    setSelectedDay(day);
                    onSelectDate && onSelectDate(viewYear, viewMonth + 1, day);
                  }
                }}
                onMouseEnter={() => !isFuture && setHoverDay(day)}
                onMouseLeave={() => setHoverDay(null)}
                style={{
                  borderRadius:8, padding:"5px 2px", cursor: isFuture ? "default" : "pointer",
                  minHeight:46,
                  background: isFuture
                    ? "transparent"
                    : isSelected ? `rgba(15,118,110,0.12)` : isHover ? `rgba(15,118,110,0.05)` : "transparent",
                  border: isSelected && !isFuture ? `1.5px solid ${C.teal}` : "1.5px solid transparent",
                  transition:"all .14s",
                  opacity: isFuture ? 0.35 : 1,
                }}>
                <div style={{ textAlign:"center", fontSize:11, fontWeight: isSelected && !isFuture ? 800 : 600,
                  color: isFuture ? C.textSoft : isSelected ? C.teal : C.text, marginBottom:2 }}>{day}</div>

                {/* Only show dots for non-future dates */}
                {!isFuture && (
                  <div style={{ display:"flex", justifyContent:"center", gap:2, flexWrap:"wrap" }}>
                    {DOT_TYPES.filter(dt => data[dt.key] > 0).map(dt => (
                      <div key={dt.key} style={{ width:4, height:4, borderRadius:"50%", background:dt.color }} />
                    ))}
                  </div>
                )}

                {/* Only show revenue preview for non-future dates on hover */}
                {!isFuture && isHover && (
                  <div style={{ fontSize:7.5, fontWeight:700, color:C.teal, textAlign:"center",
                    background:`rgba(15,118,110,0.08)`, borderRadius:3, padding:"1px 2px", marginTop:1 }}>
                    ${(data.revenue/1000).toFixed(1)}k
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ padding:"8px 16px 12px", borderTop:`1px solid ${C.border}`,
          display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center" }}>
          {DOT_TYPES.map(dt => (
            <div key={dt.key} style={{ display:"flex", alignItems:"center", gap:4 }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:dt.color }} />
              <span style={{ fontSize:10, fontWeight:600, color:C.textMid }}>{dt.label}</span>
            </div>
          ))}
        </div>
        {selectedDay && !isFutureDate(viewYear, viewMonth + 1, selectedDay) && (
          <div style={{ margin:"0 12px 12px", borderRadius:12, background:`rgba(15,118,110,0.05)`,
            border:`1px solid ${C.border}`, padding:"10px 12px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.teal, marginBottom:8 }}>
              {MONTHS[viewMonth]} {selectedDay}, {viewYear}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:5 }}>
              {[
                { label:"Placed",    val:dayData[selectedDay].placed,    color:C.teal },
                { label:"Delivered", val:dayData[selectedDay].delivered, color:"#22c55e" },
                { label:"Pending",   val:dayData[selectedDay].pending,   color:C.accent },
                { label:"Cancelled", val:dayData[selectedDay].cancelled, color:"#ef4444" },
              ].map(item => (
                <div key={item.label} style={{ textAlign:"center", padding:"7px 4px",
                  background:C.white, borderRadius:8, border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:14, fontWeight:800, color:item.color }}>{item.val}</div>
                  <div style={{ fontSize:9, color:C.textSoft, fontWeight:600, marginTop:1 }}>{item.label}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:7, padding:"7px 10px", background:C.white,
              borderRadius:8, border:`1px solid ${C.border}`, display:"flex",
              alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:11, fontWeight:600, color:C.textMid }}>Daily Revenue</span>
              <span style={{ fontSize:13, fontWeight:800, color:C.teal }}>
                {fmt(dayData[selectedDay].revenue)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FilterBar ─────────────────────────────────────────────────────────────────
function FilterBar({ filter, setFilter, activeCount, onReset }) {
  const [calOpen, setCalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";

  const yearOptions = [
    { value: "all", label: "All Years" },
    { value: 2023,  label: "2023" },
    { value: 2024,  label: "2024" },
    { value: 2025,  label: "2025" },
  ];
  const monthOptions = [
    { value:"all", label:"All Months" },
    ...MONTHS.map((m, i) => ({ value: i + 1, label: m })),
  ];

  const handleCalDate = (y, m, d) => {
    setFilter(f => ({ ...f, year: y, month: m, day: d }));
  };

  const filterControls = (
    <div style={{ display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
      <FilterDropdown label="Year"  value={filter.year}  options={yearOptions}  onChange={v=>setFilter(f=>({...f,year:v,fromDate:"",toDate:""}))}  width={120} />
      <FilterDropdown label="Month" value={filter.month} options={monthOptions} onChange={v=>setFilter(f=>({...f,month:v,fromDate:"",toDate:""}))} width={138} />
      <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
        <span style={{ fontSize:10, fontWeight:600, color:C.textSoft }}>FROM</span>
        <input type="date" value={filter.fromDate}
          onChange={e=>setFilter(f=>({...f,fromDate:e.target.value,year:"all",month:"all"}))}
          style={{
            padding:"6px 8px", borderRadius:9, fontSize:11.5, fontWeight:500, color:C.text,
            border:`1.5px solid ${filter.fromDate ? C.teal : C.border}`,
            background: filter.fromDate ? `rgba(15,118,110,0.05)` : C.white,
            outline:"none", cursor:"pointer", fontFamily:"inherit",
          }} />
        <span style={{ fontSize:10, fontWeight:600, color:C.textSoft }}>TO</span>
        <input type="date" value={filter.toDate}
          onChange={e=>setFilter(f=>({...f,toDate:e.target.value,year:"all",month:"all"}))}
          style={{
            padding:"6px 8px", borderRadius:9, fontSize:11.5, fontWeight:500, color:C.text,
            border:`1.5px solid ${filter.toDate ? C.teal : C.border}`,
            background: filter.toDate ? `rgba(15,118,110,0.05)` : C.white,
            outline:"none", cursor:"pointer", fontFamily:"inherit",
          }} />
      </div>
    </div>
  );

  return (
    <>
      <div style={{
        background: C.glass,
        backdropFilter: "blur(16px)",
        borderBottom: `1px solid ${C.glassBorder}`,
        padding: isMobile ? "8px 14px" : "9px 24px",
        position: "sticky",
        top: isMobile ? 48 : 46,
        zIndex: 90,
        boxShadow: "0 2px 12px rgba(15,118,110,0.06)",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <SlidersHorizontal size={13} color={C.teal} />
            <span style={{ fontSize:11, fontWeight:700, color:C.textMid, letterSpacing:"0.04em", textTransform:"uppercase" }}>Filters</span>
            {activeCount > 0 && (
              <span style={{ background:C.teal, color:C.white, fontSize:9, fontWeight:800,
                borderRadius:20, padding:"1px 6px" }}>{activeCount}</span>
            )}
          </div>

          {isMobile ? (
            <button onClick={() => setMobileOpen(v=>!v)} style={{
              marginLeft:4, padding:"5px 10px", borderRadius:8, border:`1.5px solid ${C.border}`,
              background:C.white, cursor:"pointer", fontSize:11, fontWeight:600, color:C.textMid,
              display:"flex", alignItems:"center", gap:4,
            }}>
              {mobileOpen ? <X size={12}/> : <ChevronDown size={12}/>}
              {mobileOpen ? "Hide" : "Show"}
            </button>
          ) : (
            <>
              <div style={{ width:1, height:24, background:C.border }} />
              {filterControls}
            </>
          )}

          <div style={{ flex:1 }} />

          {activeCount > 0 && (
            <button onClick={onReset} style={{
              display:"flex", alignItems:"center", gap:4, padding:"6px 12px", borderRadius:9,
              border:`1.5px solid ${C.border}`, background:C.white, cursor:"pointer",
              fontSize:11.5, fontWeight:600, color:C.textMid, whiteSpace:"nowrap",
            }}>
              <RefreshCcw size={11} />
              Reset
            </button>
          )}

          <button onClick={() => setCalOpen(v => !v)} style={{
            width:34, height:34, borderRadius:9, display:"flex", alignItems:"center",
            justifyContent:"center", cursor:"pointer", flexShrink:0,
            border:`1.5px solid ${calOpen ? C.teal : C.border}`,
            background: calOpen ? `rgba(15,118,110,0.08)` : C.white,
            color: calOpen ? C.teal : C.textMid,
          }}>
            <Calendar size={15} />
          </button>
        </div>

        {isMobile && mobileOpen && (
          <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.border}` }}>
            {filterControls}
          </div>
        )}
      </div>

      {calOpen && (
        <CalendarPopup filter={filter} onSelectDate={handleCalDate} onClose={() => setCalOpen(false)} />
      )}
    </>
  );
}

// ─── Grid helpers ──────────────────────────────────────────────────────────────
function KpiGrid({ children, cols }) {
  const bp = useBreakpoint();
  const gridCols = bp === "xs" ? 1 : bp === "sm" ? 2 : bp === "md" ? cols > 3 ? 2 : cols : cols;
  return (
    <div style={{ display:"grid", gridTemplateColumns:`repeat(${gridCols},1fr)`, gap:10 }}>
      {children}
    </div>
  );
}

function TwoCol({ left, right, ratio="2fr 1fr" }) {
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";
  return (
    <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":ratio, gap:12 }}>
      {left}
      {right}
    </div>
  );
}

function ThreeCol({ children }) {
  const bp = useBreakpoint();
  const cols = bp === "xs" ? 1 : bp === "sm" ? 1 : bp === "md" ? 2 : 3;
  return (
    <div style={{ display:"grid", gridTemplateColumns:`repeat(${cols},1fr)`, gap:12 }}>
      {children}
    </div>
  );
}

// ─── Tab Pages ─────────────────────────────────────────────────────────────────
function Overview({ data }) {
  const segmentData = [
    {name:"Corporate",value:30.7,color:C.teal},{name:"Small Biz",value:30.3,color:C.tealLight},
    {name:"Individual",value:22.2,color:C.accent},{name:"Government",value:11.2,color:C.purple},
    {name:"Non-Profit",value:5.6,color:"#FAE3CC"},
  ];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <KpiGrid cols={4}>
        <KpiCard label="Total Revenue"   value={fmt(data.totalRevenue)} icon={DollarSign} />
        <KpiCard label="Total Orders"    value={data.totalOrders.toString()} icon={ShoppingCart} />
        <KpiCard label="Avg Order Value" value={fmt(data.avgOrder)}     icon={BarChart3} />
        <KpiCard label="Biggest Order"   value={fmt(data.biggestOrder)} icon={Star} tint="accent" />
      </KpiGrid>
      <TwoCol
        left={
          <Card title="Monthly Revenue Trend">
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="m" tick={{ fontSize:10, fill:C.textSoft }} />
                <YAxis tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} width={44} />
                <Tooltip formatter={(v,n)=>n==="rev"?fmt(v):v} />
                <Bar dataKey="rev" fill={C.teal} radius={[4,4,0,0]} name="Revenue" />
                <Line dataKey="orders" stroke={C.accent} strokeWidth={2} dot={false} name="Orders" />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        }
        right={
          <Card title="Customer Segment Mix">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={segmentData} cx="50%" cy="44%" innerRadius={48} outerRadius={74} dataKey="value" paddingAngle={2}>
                  {segmentData.map((d,i)=><Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={v=>`${v}%`} />
                <Legend iconSize={7} wrapperStyle={{ fontSize:9.5 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        }
      />
      <TwoCol ratio="1fr 1fr"
        left={
          <Card title="Top Order Types by Revenue">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.orderTypes} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis type="number" tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" width={64} tick={{ fontSize:10, fill:C.textSoft }} />
                <Tooltip formatter={v=>fmt(v)} />
                <Bar dataKey="rev" fill={C.tealLight} radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        }
        right={
          <Card title="Service Revenue">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.serviceRevenue}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="name" tick={{ fontSize:9, fill:C.textSoft }} angle={-10} textAnchor="end" height={40} />
                <YAxis tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} width={44} />
                <Tooltip formatter={v=>fmt(v)} />
                <Bar dataKey="rev" radius={[4,4,0,0]}>
                  {data.serviceRevenue.map((_,i)=><Cell key={i} fill={BARS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        }
      />
    </div>
  );
}

function Orders({ data }) {
  const orderVolume = [
    {name:"Banners",count:Math.round(38*data.factor)},{name:"Booklets",count:Math.round(36*data.factor)},{name:"Brochures",count:Math.round(43*data.factor)},
    {name:"Bus.Cards",count:Math.round(31*data.factor)},{name:"Calendars",count:Math.round(49*data.factor)},{name:"Flyers",count:Math.round(46*data.factor)},
    {name:"Posters",count:Math.round(46*data.factor)},{name:"Stickers",count:Math.round(31*data.factor)},{name:"T-Shirts",count:Math.round(31*data.factor)},
  ];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <KpiGrid cols={4}>
        <KpiCard label="Completed" value={data.completedOrders.toString()} icon={CheckCircle} />
        <KpiCard label="Open Orders" value={data.openOrders.toString()} icon={Clock} />
        <KpiCard label="Rush Orders" value={data.rushOrders.toString()} icon={Zap} tint="accent" />
        <KpiCard label="Largest Order" value={fmt(data.biggestOrder)} icon={Star} tint="accent" />
      </KpiGrid>
      <TwoCol ratio="1fr 2fr"
        left={
          <Card title="Revenue Ranking">
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={data.orderTypes} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis type="number" tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" width={64} tick={{ fontSize:10, fill:C.textSoft }} />
                <Tooltip formatter={v=>fmt(v)} />
                <Bar dataKey="rev" fill={C.teal} radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        }
        right={
          <Card title="Order Volume by Product">
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={orderVolume}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="name" tick={{ fontSize:9, fill:C.textSoft }} angle={-30} textAnchor="end" height={48} />
                <YAxis tick={{ fontSize:10, fill:C.textSoft }} />
                <Tooltip />
                <Bar dataKey="count" fill={C.tealLight} radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        }
      />
      <Card title="Revenue (bars) vs Orders (line) — Monthly">
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={data.monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="m" tick={{ fontSize:10, fill:C.textSoft }} />
            <YAxis yAxisId="rev" tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} width={44} />
            <YAxis yAxisId="cnt" orientation="right" tick={{ fontSize:10, fill:C.textSoft }} width={30} />
            <Tooltip />
            <Bar yAxisId="rev" dataKey="rev" fill={C.teal} radius={[4,4,0,0]} name="Revenue" />
            <Line yAxisId="cnt" dataKey="orders" stroke={C.accent} strokeWidth={2} dot={{ r:3, fill:C.accent }} name="Orders" />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

function Customers({ data }) {
  const paymentData = [
    {name:"Cash",value:Math.round(149663*data.factor)},{name:"Credit Card",value:Math.round(118842*data.factor)},
    {name:"UPI",value:Math.round(114139*data.factor)},{name:"Bank Transfer",value:Math.round(63259*data.factor)},
    {name:"Debit Card",value:Math.round(99650*data.factor)},{name:"Cheque",value:Math.round(19274*data.factor)},
  ];
  const monthlySegment = data.monthly.slice(0,6).map(r=>({
    m:r.m,
    corp:Math.round(r.rev*0.302),smb:Math.round(r.rev*0.367),
    ind:Math.round(r.rev*0.119),govt:Math.round(r.rev*0.099),
  }));
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <KpiGrid cols={4}>
        <KpiCard label="Corporate Revenue" value={fmt(data.segmentRevData[0]?.rev||0)} icon={DollarSign} />
        <KpiCard label="Small Biz Revenue" value={fmt(data.segmentRevData[4]?.rev||0)} icon={DollarSign} />
        <KpiCard label="Individual Orders" value={Math.round(data.totalOrders*0.288).toString()} icon={Users} />
        <KpiCard label="Repeat Customers" value={Math.round(data.totalOrders*0.192).toString()} icon={Star} tint="accent" />
      </KpiGrid>
      <ThreeCol>
        <Card title="Segment Revenue">
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={data.segmentRevData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fontSize:9, fill:C.textSoft }} angle={-15} textAnchor="end" height={42} />
              <YAxis tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} width={44} />
              <Tooltip formatter={v=>fmt(v)} />
              <Bar dataKey="rev" radius={[4,4,0,0]}>
                {data.segmentRevData.map((_,i)=><Cell key={i} fill={BARS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Where Customers Come From">
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie data={[
                {name:"Phone",value:17.1},{name:"Referral",value:16.2},
                {name:"Repeat",value:19.6},{name:"Walk-in",value:30.3},{name:"Website",value:16.7},
              ]} cx="50%" cy="44%" innerRadius={46} outerRadius={73} dataKey="value" paddingAngle={2}>
                {[C.teal,C.tealLight,C.accent,C.purple,"#A8D5D1"].map((c,i)=><Cell key={i} fill={c} />)}
              </Pie>
              <Tooltip formatter={v=>`${v}%`} />
              <Legend iconSize={7} wrapperStyle={{ fontSize:9.5 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Payment Method">
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={paymentData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis type="number" tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" width={78} tick={{ fontSize:10, fill:C.textSoft }} />
              <Tooltip formatter={v=>fmt(v)} />
              <Bar dataKey="value" radius={[0,4,4,0]}>
                {paymentData.map((_,i)=><Cell key={i} fill={i%2===0?C.teal:C.tealLight} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </ThreeCol>
      <Card title="Monthly Segment Trend">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={monthlySegment}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="m" tick={{ fontSize:10, fill:C.textSoft }} />
            <YAxis tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} width={44} />
            <Tooltip formatter={v=>fmt(v)} />
            <Legend wrapperStyle={{ fontSize:10 }} />
            <Bar dataKey="corp" stackId="a" fill={C.teal}      name="Corporate" />
            <Bar dataKey="smb"  stackId="a" fill={C.tealLight} name="Small Biz" />
            <Bar dataKey="ind"  stackId="a" fill={C.accent}    name="Individual" />
            <Bar dataKey="govt" stackId="a" fill={C.purple}    name="Government" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

// ─── Updated Services section: Digital Print, Clothing, Mugs, Resin ───────────
function Services({ data }) {
  // Status distribution for the 4 new categories
  const statusData = [
    {service:"Digital",   pending:Math.round(30*data.factor), inProgress:Math.round(45*data.factor), completed:Math.round(100*data.factor), cancelled:Math.round(10*data.factor)},
    {service:"Clothing",  pending:Math.round(42*data.factor), inProgress:Math.round(58*data.factor), completed:Math.round(130*data.factor), cancelled:Math.round(14*data.factor)},
    {service:"Mugs",      pending:Math.round(25*data.factor), inProgress:Math.round(38*data.factor), completed:Math.round(75*data.factor),  cancelled:Math.round(8*data.factor)},
    {service:"Resin",     pending:Math.round(18*data.factor), inProgress:Math.round(22*data.factor), completed:Math.round(52*data.factor),  cancelled:Math.round(6*data.factor)},
  ];
  const totalUnits = data.serviceRevenue.reduce((s,r)=>s+r.units,0);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {/* KPI cards: one per service category */}
      <KpiGrid cols={4}>
        <KpiCard label="Digital Print" value={fmt(data.serviceRevenue[0]?.rev||0)} icon={Printer} />
        <KpiCard label="Clothing"      value={fmt(data.serviceRevenue[1]?.rev||0)} icon={Shirt} tint="purple" />
        <KpiCard label="Mugs"          value={fmt(data.serviceRevenue[2]?.rev||0)} icon={Coffee} tint="accent" />
        <KpiCard label="Resin"         value={fmt(data.serviceRevenue[3]?.rev||0)} icon={Layers} />
      </KpiGrid>
      <ThreeCol>
        {/* Service Revenue bar chart */}
        <Card title="Service Revenue">
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={data.serviceRevenue}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" tick={{ fontSize:9, fill:C.textSoft }} angle={-12} textAnchor="end" height={42} />
              <YAxis tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} width={44} />
              <Tooltip formatter={v=>fmt(v)} />
              <Bar dataKey="rev" radius={[4,4,0,0]}>
                {data.serviceRevenue.map((_,i)=><Cell key={i} fill={BARS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Status Distribution stacked bar */}
        <Card title="Status Distribution">
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="service" tick={{ fontSize:9.5, fill:C.textSoft }} />
              <YAxis tick={{ fontSize:10, fill:C.textSoft }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize:9 }} />
              <Bar dataKey="pending"    stackId="a" fill={C.accent}    name="Pending" />
              <Bar dataKey="inProgress" stackId="a" fill={C.tealLight} name="In Progress" />
              <Bar dataKey="completed"  stackId="a" fill={C.teal}      name="Completed" />
              <Bar dataKey="cancelled"  stackId="a" fill="#EF9A9A"     name="Cancelled" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Production Volume horizontal bar */}
        <Card title="Production Volume">
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={data.serviceRevenue} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis type="number" tick={{ fontSize:10, fill:C.textSoft }} tickFormatter={v=>`${(v/1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" width={78} tick={{ fontSize:9.5, fill:C.textSoft }} />
              <Tooltip formatter={v=>v.toLocaleString()+" units"} />
              <Bar dataKey="units" radius={[0,4,4,0]}>
                {data.serviceRevenue.map((_,i)=><Cell key={i} fill={BARS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </ThreeCol>

      {/* Total units summary card */}
      <KpiGrid cols={4}>
        <KpiCard label="Total Units Produced" value={totalUnits.toLocaleString()} icon={Hash} tint="accent" />
        <KpiCard label="Avg Rev / Service"     value={fmt(Math.round(data.totalRevenue / 4))} icon={BarChart3} />
        <KpiCard label="Completed Jobs"        value={data.completedOrders.toString()} icon={CheckCircle} />
        <KpiCard label="Rush Orders"           value={data.rushOrders.toString()} icon={Zap} tint="purple" />
      </KpiGrid>
    </div>
  );
}

// ─── OrderSearch ───────────────────────────────────────────────────────────────
const sampleOrders = {
  "PS-0001":{id:"PS-0001",date:"Jul 15, 2024",type:"T-Shirts",service:"Clothing",segment:"Small Business",customer:"Creative Studio",description:"Custom design",amount:1042,qty:323,payment:"Credit Card",status:"Completed"},
  "PS-0002":{id:"PS-0002",date:"Aug 3, 2024",type:"Banners",service:"Digital Print",segment:"Corporate",customer:"Tech Corp",description:"Trade show banners",amount:2840,qty:50,payment:"Bank Transfer",status:"In Progress"},
  "PS-0003":{id:"PS-0003",date:"Jun 20, 2024",type:"Mugs",service:"Mugs",segment:"Government",customer:"City Council",description:"Branded office mugs",amount:3200,qty:500,payment:"Cheque",status:"Completed"},
};

function OrderSearch() {
  const [selected, setSelected] = useState("PS-0001");
  const [open, setOpen] = useState(false);
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";
  const order = sampleOrders[selected];
  const statusColor = { Completed:C.teal, "In Progress":C.accent, Pending:C.purple };
  return (
    <div style={{ display:"flex", flexDirection:isMobile?"column":"row", gap:20 }}>
      <div style={{ width:isMobile?"100%":320, flexShrink:0 }}>
        <Card title="Order Lookup">
          <label style={{ fontSize:11, fontWeight:700, color:C.textMid, display:"block",
            marginBottom:8, textTransform:"uppercase", letterSpacing:"0.05em" }}>Select Order ID</label>
          <div style={{ position:"relative" }}>
            <button onClick={()=>setOpen(!open)} style={{ width:"100%", padding:"10px 12px",
              borderRadius:10, border:`2px solid ${open?C.teal:C.border}`, background:C.bg,
              display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer",
              fontSize:13, fontWeight:600, color:C.text }}>
              {selected}
              <ChevronDown size={14} color={C.textSoft}
                style={{ transform:open?"rotate(180deg)":"none", transition:"transform .2s" }} />
            </button>
            {open && (
              <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0,
                background:C.white, border:`1px solid ${C.border}`, borderRadius:10,
                overflow:"hidden", zIndex:10, boxShadow:"0 4px 20px rgba(0,0,0,0.1)" }}>
                {Object.keys(sampleOrders).map(k=>(
                  <div key={k} onClick={()=>{setSelected(k);setOpen(false);}}
                    style={{ padding:"10px 12px", cursor:"pointer",
                      background:selected===k?C.bg:C.white,
                      fontSize:12.5, fontWeight:selected===k?700:500, color:C.text,
                      borderBottom:`1px solid ${C.border}` }}>
                    {k} — {sampleOrders[k].customer}
                  </div>
                ))}
              </div>
            )}
          </div>
          {order && (
            <div style={{ marginTop:14, display:"grid", gridTemplateColumns:"1fr 1fr", gap:7 }}>
              {[
                ["Amount", fmt(order.amount), C.teal],
                ["Qty", order.qty.toLocaleString(), C.accent],
                ["Service", order.service, C.purple],
                ["Status", order.status, statusColor[order.status]||C.teal],
              ].map(([k,v,clr])=>(
                <div key={k} style={{ background:C.bg, borderRadius:9, padding:"9px 10px", border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:9.5, fontWeight:600, color:C.textSoft, textTransform:"uppercase",
                    letterSpacing:"0.05em", marginBottom:2 }}>{k}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:clr }}>{v}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      {order && (
        <div style={{ flex:1 }}>
          <Card>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14, flexWrap:"wrap" }}>
              <FileText size={14} color={C.teal} />
              <span style={{ fontSize:11.5, fontWeight:700, color:C.teal, textTransform:"uppercase", letterSpacing:"0.07em" }}>
                Order Details — {order.id}
              </span>
              <span style={{ marginLeft:"auto", background:statusColor[order.status]+"22", color:statusColor[order.status],
                padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>{order.status}</span>
            </div>
            <div style={{ borderRadius:10, overflow:"hidden", border:`1px solid ${C.border}` }}>
              {[
                ["Order ID",order.id],["Date",order.date],["Order Type",order.type],
                ["Service Type",order.service],["Customer Segment",order.segment],
                ["Customer Name",order.customer],["Description",order.description],
                ["Amount",fmt(order.amount)],["Quantity",order.qty.toLocaleString()],
                ["Payment Method",order.payment],
              ].map(([k,v],i)=>(
                <div key={k} style={{ display:"grid", gridTemplateColumns:"150px 1fr", background:i%2===0?C.bg:C.white }}>
                  <div style={{ padding:"9px 14px", fontSize:11, fontWeight:700, color:C.textMid,
                    borderRight:`1px solid ${C.border}` }}>{k}</div>
                  <div style={{ padding:"9px 14px", fontSize:12, color:C.text }}>{v}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── TopNav ────────────────────────────────────────────────────────────────────
const TABS = [
  { id:"overview",  label:"Overview",  icon:Home },
  { id:"orders",    label:"Orders",    icon:ShoppingCart },
  { id:"customers", label:"Customers", icon:Users },
  { id:"services",  label:"Services",  icon:Printer },
  { id:"search",    label:"Search",    icon:Search },
];

function TopNav({ active, setActive }) {
  const bp = useBreakpoint();
  const isMobile = bp === "xs" || bp === "sm";
  const [menuOpen, setMenuOpen] = useState(false);

  if (isMobile) {
    return (
      <header style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
        position:"sticky", top:0, zIndex:100, boxShadow:"0 1px 8px rgba(15,118,110,0.07)" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"0 14px", height:48 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:12, fontWeight:600, color:C.textMid }}>
              {TABS.find(t=>t.id===active)?.label}
            </span>
            <button onClick={()=>setMenuOpen(v=>!v)} style={{
              width:36, height:36, borderRadius:9, border:`1.5px solid ${C.border}`,
              background:C.white, display:"flex", alignItems:"center",
              justifyContent:"center", cursor:"pointer" }}>
              {menuOpen ? <X size={16} color={C.teal} /> : <Menu size={16} color={C.textMid} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div style={{ borderTop:`1px solid ${C.border}`, background:C.white }}>
            {TABS.map(({ id, label, icon:Icon }) => (
              <button key={id} onClick={()=>{setActive(id);setMenuOpen(false);}} style={{
                width:"100%", display:"flex", alignItems:"center", gap:10, padding:"12px 16px",
                border:"none", background: active===id ? `rgba(15,118,110,0.07)` : "transparent",
                cursor:"pointer", fontSize:13.5, fontWeight: active===id ? 700 : 500,
                color: active===id ? C.teal : C.text,
                borderLeft: active===id ? `3px solid ${C.teal}` : "3px solid transparent",
              }}>
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        )}
      </header>
    );
  }

  return (
    <header style={{ background:C.white, borderBottom:`1px solid ${C.border}`,
      position:"sticky", top:0, zIndex:100, boxShadow:"0 1px 8px rgba(15,118,110,0.07)" }}>
      <div style={{ display:"flex", alignItems:"stretch", padding:"0 24px", height:46, gap:2 }}>
        {TABS.map(({ id, label, icon:Icon }) => {
          const isAct = active === id;
          return (
            <button key={id} onClick={()=>setActive(id)} style={{
              display:"flex", alignItems:"center", gap:6, padding:"0 14px",
              border:"none", cursor:"pointer", background:"transparent",
              color: isAct ? C.teal : C.textMid,
              fontWeight: isAct ? 700 : 500, fontSize:12.5,
              borderBottom: isAct ? `2.5px solid ${C.teal}` : "2.5px solid transparent",
              transition:"color .15s, border-color .15s",
              whiteSpace:"nowrap",
            }}>
              <Icon size={13} />
              {label}
            </button>
          );
        })}
      </div>
    </header>
  );
}

// ─── ActiveFilterBanner ────────────────────────────────────────────────────────
function ActiveFilterBanner({ filter }) {
  const parts = [];
  if (String(filter.year) !== "all") parts.push(`Year: ${filter.year}`);
  if (String(filter.month) !== "all") parts.push(`Month: ${MONTHS[Number(filter.month)-1]}`);
  if (filter.fromDate) parts.push(`From: ${filter.fromDate}`);
  if (filter.toDate) parts.push(`To: ${filter.toDate}`);
  if (parts.length === 0) return null;
  return (
    <div style={{
      background:`linear-gradient(90deg, rgba(15,118,110,0.08) 0%, rgba(20,184,166,0.05) 100%)`,
      borderBottom:`1px solid rgba(15,118,110,0.12)`,
      padding:"6px 24px",
      display:"flex", alignItems:"center", gap:7, flexWrap:"wrap",
    }}>
      <Filter size={10} color={C.teal} />
      <span style={{ fontSize:10.5, fontWeight:700, color:C.teal, letterSpacing:"0.04em" }}>ACTIVE:</span>
      {parts.map(p => (
        <span key={p} style={{
          background:C.white, border:`1px solid rgba(15,118,110,0.2)`, borderRadius:20,
          padding:"2px 9px", fontSize:10.5, fontWeight:600, color:C.tealDark,
        }}>{p}</span>
      ))}
    </div>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState("overview");
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const bp = useBreakpoint();

  const data = deriveData(filter);

  // Count active filters (comparing against DEFAULT_FILTER values)
  const activeCount = [
    String(filter.year)  !== String(DEFAULT_FILTER.year),
    String(filter.month) !== String(DEFAULT_FILTER.month),
    filter.fromDate !== DEFAULT_FILTER.fromDate,
    filter.toDate   !== DEFAULT_FILTER.toDate,
  ].filter(Boolean).length;

  // Reset brings everything back to today's date
  const handleReset = () => setFilter(DEFAULT_FILTER);

  const pageMap = {
    overview:  <Overview  data={data} />,
    orders:    <Orders    data={data} />,
    customers: <Customers data={data} />,
    services:  <Services  data={data} />,
    search:    <OrderSearch />,
  };

  const padding = bp === "xs" ? "16px 12px" : bp === "sm" ? "16px 14px" : "20px 24px";

  return (
    <>
      <style>{`
        @keyframes dropIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes popIn  { from { opacity:0; transform:scale(0.94) translateY(-10px); } to { opacity:1; transform:scale(1) translateY(0); } }
        * { box-sizing: border-box; margin:0; padding:0; }
        input[type="date"] { font-family: inherit; }
        input[type="date"]:focus { border-color: #0F766E !important; box-shadow: 0 0 0 3px rgba(15,118,110,0.12); outline: none; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
      `}</style>
      <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
        <TopNav active={tab} setActive={setTab} />
        <FilterBar filter={filter} setFilter={setFilter} activeCount={activeCount} onReset={handleReset} />
        <ActiveFilterBanner filter={filter} />
        <main style={{ padding, maxWidth:1400, margin:"0 auto" }}>
          {pageMap[tab]}
        </main>
      </div>
    </>
  );
}